<template>
  <div class="p-4 bg-gray-100 min-h-screen">
    <Loader v-if="loading" />
    <Card class="shadow-2 surface-card border-round p-4">
      <template v-slot:title>
        <div class="flex justify-content-between align-items-center">
          <div class="text-2xl font-bold text-gray-800 flex align-items-center">
            <i class="pi pi-calendar-plus mr-2 text-primary"></i>
            {{ showMese ? 'Pagamenti nel mese' : 'Incassi giornalieri' }}
          </div>
          <Button @click="showMese = !showMese" :label="showMese ? 'Vedi Giorno' : 'Vedi Mese'" icon="pi pi-sync" class="p-button-danger p-button-sm" />
        </div>
      </template>

      <template v-slot:content>
        <div v-if="showMese">
          <div class="flex justify-content-between align-items-center mb-4">
            <Button icon="pi pi-angle-left" class="p-button-rounded p-button-text p-button-secondary" @click="prevMonth" />
            <h2 class="text-xl font-medium text-gray-800">{{ formattedMonth }}</h2>
            <Button icon="pi pi-angle-right" class="p-button-rounded p-button-text p-button-secondary" @click="nextMonth" />
          </div>

          <div class="grid p-fluid">
            <div class="col-12 md:col-6">
              <Card class="bg-red-600 text-white text-center shadow-1">
                <template #title>Da pagare</template>
                <template #content>
                  <div class="text-4xl font-bold">{{ totalToPay }} €</div>
                </template>
              </Card>
            </div>
            <div class="col-12 md:col-6">
              <Card class="bg-green-600 text-white text-center shadow-1">
                <template #title>Pagato</template>
                <template #content>
                  <div class="text-4xl font-bold">{{ totalePagato.toFixed(2) }} €</div>
                </template>
              </Card>
            </div>
          </div>
          
          <h4 class="mt-4 mb-2 text-lg font-semibold text-gray-800">Riepilogo Incassi per Conto</h4>
          <div class="grid p-fluid">
            <div v-for="cassa in casse" :key="cassa.type_pay" class="col-12 md:col-6 lg:col-2">
              <Card class="surface-card shadow-1 p-3 border-round">
                <template #title>
                  <div class="flex justify-content-between align-items-center">
                    <span class="text-xl font-bold">{{ cassa.type_pay }}</span>
                    <i :class="{'pi pi-money-bill': cassa.type_pay === 'Contanti', 'pi pi-credit-card': cassa.type_pay !== 'Contanti'}" class="text-2xl text-primary"></i>
                  </div>
                </template>
                <template #content>
                    <div class="text-3xl font-bold text-green-600 text-right">{{ cassa.total.toFixed(2) }} €</div>
                </template>
              </Card>
            </div>
            <div v-if="casse.length === 0" class="col-12">
                <div class="text-center text-sm text-gray-400 py-3 surface-card border-round shadow-1">Nessun incasso registrato nel mese.</div>
            </div>
          </div>
          
          <!-- Payment Charts Section -->
          <div class="grid p-fluid mt-4" v-if="chartDataCategory.corsi > 0 || chartDataCategory.prodotti > 0">
            <div class="col-12">
              <h4 class="mb-2 text-lg font-semibold text-gray-800">Statistiche Pagamenti</h4>
            </div>
            <div class="col-12 md:col-6">
              <Card class="surface-card shadow-1">
                <template #title>
                  <div class="text-center text-lg font-bold">Corsi vs Prodotti</div>
                </template>
                <template #content>
                  <canvas ref="chartCategory" style="max-height: 300px;"></canvas>
                </template>
              </Card>
            </div>
            <div class="col-12 md:col-6" v-if="chartDataProducts.length > 0">
              <Card class="surface-card shadow-1">
                <template #title>
                  <div class="text-center text-lg font-bold">Dettaglio Prodotti</div>
                </template>
                <template #content>
                  <canvas ref="chartProducts" style="max-height: 300px;"></canvas>
                  
                  <!-- Products Table -->
                  <div class="mt-4">
                    <table class="w-full">
                      <thead>
                        <tr class="text-left text-sm text-gray-500 border-bottom-1 surface-border">
                          <th class="py-2">Prodotto</th>
                          <th class="py-2 text-right">Importo</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(product, index) in sortedProducts" :key="index" class="border-bottom-1 surface-border">
                          <td class="py-2 text-sm">{{ product.product }}</td>
                          <td class="py-2 text-sm text-right font-medium">€ {{ product.total.toFixed(2) }}</td>
                        </tr>
                        <tr class="border-top-2 surface-border">
                          <td class="py-2 text-sm font-bold">Totale</td>
                          <td class="py-2 text-sm text-right font-bold">€ {{ totalProducts.toFixed(2) }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </template>
              </Card>
            </div>
          </div>
          <div class="grid p-fluid">
            <div class="col-12 md:col-6">
              <h4 class="mt-4 mb-2 text-md font-semibold text-gray-700">Dettaglio da pagare</h4>
              <div class="surface-card p-3 border-round shadow-1">
                <table class="w-full">
                  <thead>
                    <tr class="text-left text-sm text-gray-500">
                      <th class="py-2">Data scadenza</th>
                      <th class="py-2">Corso</th>
                      <th class="py-2">Utente</th>
                      <th class="py-2 text-right">Importo</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="payment in topay" :key="payment.id" class="border-top-1 surface-border">
                      <td class="py-2 text-sm">{{ moment(payment.data_scadenza).format('DD/MM/YYYY') }}</td>
                      <td @click="goToCorso(payment.id_corso)" class="py-2 text-sm text-primary underline cursor-pointer">{{ payment.corso }}</td>
                      <td @click="goToAtleta(payment.id_atleta)" class="py-2 text-sm text-primary underline cursor-pointer">{{ capitalize(payment.atleta) }}</td>
                      <td class="py-2 text-sm text-right font-medium">{{ payment.importo }} €</td>
                    </tr>
                  </tbody>
                </table>
                <div v-if="topay.length === 0" class="text-center text-sm text-gray-400 py-3">Nessun pagamento in scadenza.</div>
              </div>
            </div>

            <div class="col-12 md:col-6">
              <h4 class="mt-4 mb-2 text-md font-semibold text-gray-700">Dettaglio pagamenti</h4>
              <div class="surface-card p-3 border-round shadow-1">
                <table class="w-full">
                  <thead>
                    <tr class="text-left text-sm text-gray-500">
                      <th class="py-2">Data</th>
                      <th class="py-2">Corso</th>
                      <th class="py-2">Utente</th>
                      <th class="py-2 text-right">Importo</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="payment in payments" :key="payment.id" class="border-top-1 surface-border">
                      <td class="py-2 text-sm">{{ moment(payment.data_pagamento).format('DD/MM/YYYY') }}</td>
                      <td class="py-2 text-sm">
                        <span v-if="payment.corso" @click="goToCorso(payment.id_corso)" class="text-primary underline cursor-pointer">{{ payment.corso }}</span>
                        <span v-else>{{ payment.dettaglio_prodotto || 'Vendita' }}</span>
                        <i v-if="payment.note_pagamento" class="pi pi-info-circle ml-2 text-gray-500" :title="payment.note_pagamento" v-tooltip="payment.note_pagamento"></i>
                      </td>
                      <td @click="goToAtleta(payment.id_atleta)" class="py-2 text-sm text-primary underline cursor-pointer">{{ capitalize(payment.atleta) }}</td>
                      <td class="py-2 text-sm text-right font-medium">{{ payment.importo }} €</td>
                    </tr>
                  </tbody>
                </table>
                <div v-if="payments.length === 0" class="text-center text-sm text-gray-400 py-3">Nessun pagamento registrato.</div>
              </div>
            </div>
          </div>
        </div>

        <div v-else>
          <div class="flex justify-content-center align-items-center mb-4">
            <Calendar v-model="currentDay" dateFormat="dd/mm/yy" showIcon :showOnFocus="false" @change="onChangeDay()" />
          </div>

          <Card class="bg-green-600 text-white text-center mb-4 shadow-1">
            <template #title>Incasso totale</template>
            <template #content>
              <div class="text-4xl font-bold">{{ totalePagato.toFixed(2) }} €</div>
            </template>
          </Card>
          
          <h4 class="mb-2 text-lg font-semibold text-gray-800">Dettaglio Incassi per cassa e operatore</h4>
          <div class="grid p-fluid">
            <div v-for="cassa in casse" :key="cassa.type_pay" class="col-12 md:col-6 lg:col-4">
              <Card class="surface-card shadow-1 p-3 border-round">
                <template #title>
                  <div class="flex justify-content-between align-items-center">
                    <span class="text-xl font-bold">{{ cassa.type_pay }}</span>
                    <span class="text-lg font-semibold text-green-600">{{ cassa.total.toFixed(2) }} €</span>
                  </div>
                </template>
                <template #content>
                  <table class="w-full mt-2">
                    <thead>
                      <tr class="text-left text-sm text-gray-500">
                        <th class="py-2">Operatore</th>
                        <th class="py-2 text-right">Totale</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="operatore in cassa.operatori" :key="operatore.operatore" class="border-top-1 surface-border">
                        <td class="py-2 text-sm">{{ operatore.operatore }}</td>
                        <td class="py-2 text-sm text-right font-medium">{{ operatore.total.toFixed(2) }} €</td>
                      </tr>
                    </tbody>
                  </table>
                </template>
              </Card>
            </div>
          </div>
          
          <h4 class="mt-4 mb-2 text-lg font-semibold text-gray-800">Dettaglio pagamenti</h4>
          <div class="surface-card p-3 border-round shadow-1">
            <table class="w-full">
              <thead>
                <tr class="text-left text-sm text-gray-500">
                  <th class="py-2">Data</th>
                  <th class="py-2">Dettaglio</th>
                  <th class="py-2">Atleta</th>
                  <th class="py-2">Conto</th>
                  <th class="py-2">Origine</th>
                  <th class="py-2">Operatore</th>
                  <th class="py-2 text-right">Importo</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="payment in payments" :key="payment.id" class="border-top-1 surface-border">
                  <td class="py-2 text-sm">{{ moment(payment.data_pagamento).format('DD/MM/YYYY') }}</td>
                  <td class="py-2 text-sm">
                    <span v-if="payment.corso" @click="goToCorso(payment.id_corso)" class="text-primary underline cursor-pointer">{{ payment.corso }}</span>
                    <span v-else>{{ payment.dettaglio_prodotto || 'Vendita' }}</span>
                    <i v-if="payment.note_pagamento" class="pi pi-info-circle ml-2 text-gray-500" :title="payment.note_pagamento" v-tooltip="payment.note_pagamento"></i>
                  </td>
                  <td @click="goToAtleta(payment.id_atleta)" class="py-2 text-sm text-primary underline cursor-pointer">{{ capitalize(payment.atleta) }}</td>
                  <td class="py-2 text-sm">{{ payment.type_pay }}</td>
                  <td class="py-2 text-sm">{{ payment.type_source }}</td>
                  <td class="py-2 text-sm">{{ payment.operatore }}</td>
                  <td class="py-2 text-sm text-right font-medium">{{ payment.importo.toFixed(2) }} €</td>
                </tr>
              </tbody>
            </table>
            <div v-if="payments.length === 0" class="text-center text-sm text-gray-400 py-3">Nessun pagamento registrato.</div>
          </div>
        </div>
      </template>
    </Card>
  </div>
</template>
<script>
import { ref, onMounted, getCurrentInstance, computed, watchEffect, nextTick } from 'vue';
import Loader from "@/components/Loader.vue";
import Service from '@/services/levante-api/levante-api'
import moment from 'moment';
import { Chart, ArcElement, Tooltip, Legend, PieController, BarElement, CategoryScale, LinearScale, BarController } from 'chart.js';

Chart.register(ArcElement, Tooltip, Legend, PieController, BarElement, CategoryScale, LinearScale, BarController);

export default {
  components: {
    Loader,
  },
  setup(){
    const self = getCurrentInstance();
    const currentMonth = ref(new Date());
    const currentDay = ref(new Date());
    const payments = ref([]);
    const topay = ref([]);
    const loading = ref(true);
    const totalePagato = ref(0);
    const casse = ref([])
    const casseOperatore = ref([])
    const totalToPay = ref(0);
    const showMese = ref(false)
    self.$eventBus = self.appContext.config.globalProperties.$eventBus;
    const chartCategory = ref(null);
    const chartProducts = ref(null);
    const chartCategoryInstance = ref(null);
    const chartProductsInstance = ref(null);
    const chartDataCategory = ref({ corsi: 0, prodotti: 0 });
    const chartDataProducts = ref([]);
    const service = new Service(self.$eventBus);
    const goToAtleta = (idatleta)=> {
      console.log(idatleta)
      window.open(`/atleta/${idatleta}/dettagli`, '_blank')
    }
    const goToCorso = (idatleta)=> {
      console.log(idatleta)
      window.open(`/corso/${idatleta}/dettagli`, '_blank')
    }
    const fetchPayments = async () => {
      loading.value = true;
      try {
        var res;

        if (showMese.value){
          res = await service.getPayments(currentMonth.value.getMonth()+1, currentMonth.value.getFullYear());
        }else{
          res = await service.getPaymentsDay(moment(currentDay.value).format('YYYY-MM-DD'));
        }
        payments.value = res.items;
        totalePagato.value = payments.value.reduce((sum, item) => sum + item.importo, 0);
        
        // Calcolo totale per cassa e operatore (funziona per entrambe le viste)
        casse.value=[]
        payments.value.forEach(prop => {
          // Calcolo totale per cassa
          let cassa = casse.value.find(c => c.type_pay === prop.type_pay);
          if (!cassa) {
            cassa = { type_pay: prop.type_pay, total: 0, operatori: [] }; // Aggiungi 'operatori' per la tabella
            casse.value.push(cassa);
          }
          cassa.total += prop.importo;

          // Calcolo totale per operatore all'interno della cassa (necessario solo per il giorno)
          let cassaOp = cassa.operatori.find(op => 
            op.type_pay === prop.type_pay && op.operatore === prop.operatore
          );
          
          if (!cassaOp) {
            cassaOp = { type_pay: prop.type_pay, operatore: prop.operatore, total: 0 };
            cassa.operatori.push(cassaOp);
          }
          cassaOp.total += prop.importo;
        });
      } catch (error) {
        console.log(error);
      } finally {
        loading.value = false;
      }
      if (showMese.value) {
        fetchChartData();
      }
      fetchToPay();
    };
    const fetchToPay = async () => {
      loading.value = true;
      try {
        if (showMese.value){
          const res = await service.getToPay(currentMonth.value.getMonth()+1, currentMonth.value.getFullYear());
          topay.value = res.items;
          totalToPay.value = topay.value.reduce((sum, item) => sum + item.importo, 0);
          console.log(topay);
        } else {
          // Quando si passa alla visualizzazione giornaliera, azzera 'totalToPay' per coerenza
          totalToPay.value = 0;
          topay.value = [];
        }
      } catch (error) {
        console.log(error);
      } finally {
        loading.value = false;
      }
    };

    const fetchChartData = async () => {
      try {
        const [categoryRes, productsRes] = await Promise.all([
          service.getPaymentStatsByCategory(currentMonth.value.getMonth() + 1, currentMonth.value.getFullYear()),
          service.getPaymentStatsByProduct(currentMonth.value.getMonth() + 1, currentMonth.value.getFullYear())
        ]);
        
        chartDataCategory.value = categoryRes.items;
        chartDataProducts.value = productsRes.items;
        
        await nextTick();
        renderCharts();
      } catch (error) {
        console.error('Error loading chart data:', error);
      }
    };

    const renderCharts = () => {
      // Destroy existing charts
      if (chartCategoryInstance.value) {
        chartCategoryInstance.value.destroy();
      }
      if (chartProductsInstance.value) {
        chartProductsInstance.value.destroy();
      }

      // Render Category Chart (Corsi vs Prodotti)
      if (chartCategory.value && (chartDataCategory.value.corsi > 0 || chartDataCategory.value.prodotti > 0)) {
        const ctx = chartCategory.value.getContext('2d');
        chartCategoryInstance.value = new Chart(ctx, {
          type: 'pie',
          data: {
            labels: ['Corsi', 'Prodotti'],
            datasets: [{
              data: [chartDataCategory.value.corsi, chartDataCategory.value.prodotti],
              backgroundColor: ['#0288D1', '#689f38'],
              borderWidth: 2,
              borderColor: '#fff'
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: true,
            plugins: {
              legend: {
                position: 'bottom',
                labels: {
                  padding: 15,
                  font: {
                    size: 12
                  }
                }
              },
              tooltip: {
                callbacks: {
                  label: function(context) {
                    const label = context.label || '';
                    const value = context.parsed || 0;
                    return label + ': €' + value.toFixed(2);
                  }
                }
              }
            }
          }
        });
      }

      // Render Products Chart (Bar Chart)
      if (chartProducts.value && chartDataProducts.value.length > 0) {
        const ctx = chartProducts.value.getContext('2d');
        const colors = [
          '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF',
          '#FF9F40', '#FF6384', '#C9CBCF', '#4BC0C0', '#FF6384'
        ];
        
        chartProductsInstance.value = new Chart(ctx, {
          type: 'bar',
          data: {
            labels: chartDataProducts.value.map(p => p.product),
            datasets: [{
              label: 'Importo (€)',
              data: chartDataProducts.value.map(p => p.total),
              backgroundColor: colors.slice(0, chartDataProducts.value.length),
              borderWidth: 1,
              borderColor: '#fff'
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: true,
            plugins: {
              legend: {
                display: false
              },
              tooltip: {
                callbacks: {
                  label: function(context) {
                    const value = context.parsed.y || 0;
                    return 'Importo: €' + value.toFixed(2);
                  }
                }
              }
            },
            scales: {
              y: {
                beginAtZero: true,
                ticks: {
                  callback: function(value) {
                    return '€' + value.toFixed(0);
                  }
                }
              },
              x: {
                ticks: {
                  maxRotation: 45,
                  minRotation: 45
                }
              }
            }
          }
        });
      }
    };

    const formattedMonth = computed(() => {
      console.log(currentMonth)
      return currentMonth.value.toLocaleDateString('it-IT', { year: 'numeric', month: 'long' }).toUpperCase();
    });

    const prevMonth = () => {
      currentMonth.value = new Date(currentMonth.value.setMonth(currentMonth.value.getMonth() - 1));
    };

    const nextMonth = () => {
      currentMonth.value = new Date(currentMonth.value.setMonth(currentMonth.value.getMonth() + 1));
    };

    const onChangeDay = () =>{
      currentDay.value=moment(currentDay.value).format('DD/MM/YYYY')
      fetchPayments()
    }
    
    // Un watchEffect per chiamare fetchPayments ogni volta che cambiano showMese, currentMonth o currentDay
    watchEffect(() => {
      // In base a showMese, cambia la dipendenza attiva.
      if (showMese.value) {
        // Dipende da currentMonth (triggera se cambiano mese/anno)
        const monthYear = currentMonth.value.getFullYear() + '-' + currentMonth.value.getMonth();
        fetchPayments();
      } else {
        // Dipende da currentDay (triggera se cambia il giorno)
        const dayString = currentDay.value.toISOString().substring(0, 10);
        fetchPayments();
      }
    });

    const capitalize = (str) => {
      if (!str) return '';
      return str
        .toLowerCase()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    };

    const totalProducts = computed(() => {
      return chartDataProducts.value.reduce((sum, product) => sum + product.total, 0);
    });

    const sortedProducts = computed(() => {
      return [...chartDataProducts.value].sort((a, b) => b.total - a.total);
    });

    return {
      moment,
      currentMonth,
      goToCorso,
      payments,capitalize,
      topay,
      loading,
      formattedMonth,
      prevMonth,
      nextMonth,
      totalePagato,
      totalToPay,
      goToAtleta,
      currentDay,
      showMese,
      casse,
      casseOperatore,
      onChangeDay,
      chartCategory,
      chartProducts,
      chartDataCategory,
      chartDataProducts,
      totalProducts,
      sortedProducts
    };
  },
};
</script>

<style scoped>
/* Stili esistenti */
.month-selector {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

table {
  width: 100%;
  border-collapse: collapse;
}

th, td {
  /* Rimosso border per evitare doppie linee con primevue surface-border, ma puoi riattivarlo se necessario */
  /* border: 1px solid #ddd; */
  padding: 8px;
}

th {
  background-color: #f2f2f2;
}
.totals-container {
  display: flex;
  flex-wrap: wrap;
  gap: 10px; /* Spazio tra i box */
}

.total-box {
  flex: 1;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 5px;
  text-align: center;
  background-color: #f9f9f9;
  color:black
}
@media (max-width: 600px) {
  .total-box {
    flex: 100%;
  }
}
.bg-red-600{
  background-color: #dc2626 !important;
}
</style>